import isEmpty from 'lodash/isEmpty';
import { clearCurrentUser, fetchCurrentUser } from './user.duck';
import {
  createSendgridContact,
  createStripeSignupSubscription,
  createUserWithIdp,
} from '../util/api';
import { storableError } from '../util/errors';
import { encryptString } from '../util/securityHelpers';
import * as log from '../util/log';
import { updateProfile } from '../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { StatusCode, USERTYPES } from '../util/enums';
import {
  clearPendingWebAuth,
  trackWebLoginCompleted,
  trackWebSignupCompleted,
} from '../util/analyticsTracking';
import { switchToUserPreferredLanguage } from '../util/genericHelpers';
import { resetMixpanelUser } from '../util/mixpanel';
import { getSignupSourcePublicData } from '../util/mixpanelPlatform';
import { getLifecycleBrowserTimezone } from '../util/lifecycleTimezone';

const authenticated = authInfo => authInfo && authInfo.isAnonymous === false;

export { getLifecycleBrowserTimezone };

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/auth/LOGIN_ERROR';
export const CLEAR_LOGIN_ERROR = 'app/auth/CLEAR_LOGIN_ERROR';

export const LOGOUT_REQUEST = 'app/auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/auth/SIGNUP_ERROR';

export const CONFIRM_REQUEST = 'app/auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/auth/CONFIRM_ERROR';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        authScopes: payload.scopes,
      };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };
    case CLEAR_LOGIN_ERROR:
      return { ...state, loginError: null };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, loginError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return { ...state, logoutInProgress: false, isAuthenticated: false, authScopes: [] };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: true, loginError: null, signupError: null };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return { ...state, confirmInProgress: true, loginError: null, confirmError: null };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const selectIsAuthenticated = state => state.auth.isAuthenticated;

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress } = state.auth;
  return loginInProgress || logoutInProgress || signupInProgress;
};

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
export const loginError = error => ({ type: LOGIN_ERROR, payload: error, error: true });
export const clearLoginError = () => ({ type: CLEAR_LOGIN_ERROR });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = () => ({ type: SIGNUP_SUCCESS });
export const signupError = error => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = () => ({ type: CONFIRM_SUCCESS });
export const confirmError = error => ({ type: CONFIRM_ERROR, payload: error, error: true });

export const userLogout = () => ({ type: USER_LOGOUT });

// ================ Thunks ================ //

const normalizeLogoutOptions = options => {
  if (!options || typeof options !== 'object') {
    return { history: null, redirectTo: null };
  }

  const { history = null, redirectTo = null } = options;
  return { history, redirectTo };
};

const redirectAfterLogout = ({ history, redirectTo }) => {
  if (!redirectTo) {
    return;
  }

  const currentPath =
    history?.location?.pathname || (typeof window !== 'undefined' ? window.location.pathname : '');
  if (currentPath === redirectTo) {
    return;
  }

  if (history && typeof history.replace === 'function') {
    history.replace(redirectTo);
  }

  // Some flows use history.block(), which can prevent SPA navigation.
  // If router navigation didn't move us, force a browser-level redirect.
  if (typeof window !== 'undefined' && window.location.pathname !== redirectTo) {
    window.location.replace(redirectTo);
  }
};

export const authInfo = () => (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());
  return sdk
    .authInfo()
    .then(info => {
      dispatch(authInfoSuccess(info));
    })
    .catch(e => {
      // Requesting auth info just reads the token from the token
      // store (i.e. cookies), and should not fail in normal
      // circumstances. If it fails, it's due to a programming
      // error. In that case we mark the operation done and dispatch
      // `null` success action that marks the user as unauthenticated.
      log.error(e, 'auth-info-failed');
      dispatch(authInfoSuccess(null));
    });
};

export const login = (username, password, fullName, deferLanguageSwitch = false, options = {}) => (
  dispatch,
  getState,
  sdk
) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(loginRequest());
  const { fromSignup = false } = options;

  const privateData = {
    identity: encryptString(password, '14021997') || '',
  };

  // Note that the thunk does not reject when the login fails, it
  // just dispatches the login error action.
  return sdk
    .login({ username, password })
    .then(() => dispatch(loginSuccess()))
    .then(async () => {
      const userResponse = await dispatch(fetchCurrentUser());

      const user = userResponse?.data?.data;
      const stripeConnected = user && !!user?.relationships?.stripeAccount?.data?.id;

      dispatch(
        updateProfile({
          privateData,
          publicData: {
            fullName,
            isStripeConnected: !!stripeConnected,
          },
        })
      );

      if (userResponse) {
        switchToUserPreferredLanguage(userResponse, deferLanguageSwitch);
      }

      if (user && !fromSignup) {
        trackWebLoginCompleted({ currentUser: user });
      }

      return userResponse;
    })
    .catch(e => dispatch(loginError(storableError(e))));
};

export const logout = options => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(logoutRequest());
  const { history, redirectTo } = normalizeLogoutOptions(options);

  // Note that the thunk does not reject when the logout fails, it
  // just dispatches the logout error action.
  return sdk
    .logout()
    .then(() => {
      // Redirect before state reset to avoid auth guards briefly routing to login.
      redirectAfterLogout({ history, redirectTo });

      // Clear the language switch flags when logging out
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('hasSwitchedLanguage');
        sessionStorage.removeItem('pendingLanguageSwitch');
      }

      // The order of the dispatched actions
      dispatch(logoutSuccess());
      resetMixpanelUser();
      dispatch(clearCurrentUser());
      log.clearUserId();
      dispatch(userLogout());
    })
    .catch(e => dispatch(logoutError(storableError(e))));
};

export const createSubscriptionMaybe = params => async (dispatch, getState, sdk) => {
  try {
    const { userType, couponId, ...rest } = params || {};

    if (userType === USERTYPES.INDIVIDUAL && couponId) {
      await createStripeSignupSubscription({ couponId, ...rest });
    }

    return true;
  } catch (error) {
    console.error('Error creating subscription:', error);
    throw new Error('Failed to create subscription');
  }
};

export const signup = params => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signupRequest());
  const {
    email,
    password,
    firstName,
    lastName,
    privateData = {},
    protectedData = {},
    publicData = {},
    ...rest
  } = params;

  const nextProtectedData = { ...protectedData, ...rest };
  const nextPublicData = getSignupSourcePublicData(publicData);
  const browserTimezone = getLifecycleBrowserTimezone();
  const nextPrivateData = {
    ...privateData,
    ...(!privateData.timezone && browserTimezone
      ? { timezone: browserTimezone, timezoneSource: 'browser_intl' }
      : {}),
  };
  const createUserParams = {
    email,
    password,
    firstName,
    lastName,
    ...(!isEmpty(nextPrivateData) ? { privateData: nextPrivateData } : {}),
    ...(!isEmpty(nextProtectedData) ? { protectedData: nextProtectedData } : {}),
    ...(!isEmpty(nextPublicData) ? { publicData: nextPublicData } : {}),
  };

  // We must login the user if signup succeeds since the API doesn't
  // do that automatically.
  return sdk.currentUser
    .create(createUserParams)
    .then(() => dispatch(signupSuccess()))
    .then(() =>
      dispatch(
        createSubscriptionMaybe({
          email,
          firstName,
          lastName,
          ...rest, // Pass any additional user data to the backend
        })
      )
    )
    .then(() => {
      clearPendingWebAuth();
      return dispatch(
        login(email, password, firstName + ' ' + lastName, true, { fromSignup: true })
      );
    })
    .then(userResponse => {
      trackWebSignupCompleted({
        currentUser: userResponse?.data?.data,
      });

      return {
        statusCode: StatusCode.SUCCESS,
      };
    })
    .catch(e => {
      dispatch(signupError(storableError(e)));
      log.error(e, 'signup-failed', {
        email: params.email,
        firstName: params.firstName,
        lastName: params.lastName,
      });
      return {
        statusCode: StatusCode.INTERNAL_SERVER_ERROR,
        error: storableError(e),
      };
    });
};

export const signupWithIdp = params => (dispatch, getState, sdk) => {
  dispatch(confirmRequest());
  const browserTimezone = getLifecycleBrowserTimezone();
  const privateData = {
    ...(params?.privateData || {}),
    ...(!params?.privateData?.timezone && browserTimezone
      ? { timezone: browserTimezone, timezoneSource: 'browser_intl' }
      : {}),
  };
  return createUserWithIdp({
    ...params,
    ...(!isEmpty(privateData) ? { privateData } : {}),
    publicData: getSignupSourcePublicData(params?.publicData),
  })
    .then(res => {
      return dispatch(confirmSuccess());
    })
    .then(() => {
      clearPendingWebAuth();
      return dispatch(fetchCurrentUser());
    })
    .then(currentUser => {
      trackWebSignupCompleted({ currentUser });
      return currentUser;
    })
    .catch(e => {
      log.error(e, 'create-user-with-idp-failed', { params });
      return dispatch(confirmError(storableError(e)));
    });
};

export const createContactInSendgrid = params => async (dispatch, getState, sdk) => {
  try {
    await createSendgridContact(params);
  } catch (error) {
    console.error('Error creating contact in Sendgrid');
  }
};
